import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useEffect,
  useState,
  useMemo,
} from "react";
import ReactDOM from "react-dom/client";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  PaperProps,
  Typography,
} from "@mui/material";
import { showDialog } from "@widgets/CommonDialog";
import { globalMenu } from "@shared/libs/Global";
import CloseIcon from "@mui/icons-material/Close";
import Draggable from "react-draggable";
import { Rnd } from "react-rnd";

interface CommonModalProps {
  title: string;
  onClose: (result: any) => void;
  children?: React.ReactNode;
  validation?: boolean;
  width?: string;
  height?: string;
  okayBtn?: boolean;
  closeBtn?: boolean;
  checkData?: boolean;
  okayBtnText?: string;
  closeBtnText?: string;
}

interface ResizableModalProps {
  title: string;
  onClose: (result: any) => void;
  children?: React.ReactNode;
  validation?: boolean;
  width?: string;
  height?: string;
  okayBtn?: boolean;
  closeBtn?: boolean;
  okayBtnText?: string;
  closeBtnText?: string;
  rememberSize?: boolean;
  rememberSizeKey?: string;
}

export interface PopupResponse<T = any> {
  closeFlag: boolean; // 팝업 닫을지 여부
  msgType?: "success" | "alter" | "error"; // 메시지 종류
  msgText?: string; // 메시지 내용
  code?: string; // 코드
  rtnData?: T; // 메뉴로 전달할 데이터
  postCode?: string; // 후처리용 코드
}

function PaperComponent(props: PaperProps) {
  const nodeRef = React.useRef<HTMLDivElement>(null);
  return (
    <Draggable
      nodeRef={nodeRef as React.RefObject<HTMLDivElement>}
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} ref={nodeRef} />
    </Draggable>
  );
}

/** Resizable 팝업과 화면 가장자리 사이 최소 여백(px) */
const POPUP_GAP = 20;

function ResizablePaperComponent(
  props: PaperProps,
  width: string,
  height: string,
  x: number,
  y: number,
  onResizeStop?: (width: number, height: number) => void
) {
  return (
    <Rnd
      default={{
        x: x,
        y: y,
        width: width,
        height: height,
      }}
      // bounds="window"
      dragHandleClassName="draggable-modal"
      onResizeStop={(_, __, ref) => {
        onResizeStop?.(ref.offsetWidth, ref.offsetHeight);
      }}
    >
      <Paper {...props} style={{ width: "100%", height: "100%" }} />
    </Rnd>
  );
}

const CommonModal = forwardRef(
  (
    {
      title,
      onClose,
      children,
      validation,
      width,
      height,
      okayBtn = true,
      closeBtn = true,
      checkData = true,
      okayBtnText,
      closeBtnText,
    }: CommonModalProps,
    modalRef: any
  ) => {
    const internalRef = useRef<any>(null);
    const dialogContentRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(modalRef, () => ({
      handleConfirm: (flag: boolean, postResult?: any) =>
        handleConfirm(flag, postResult),
      getPopupParent: () => dialogContentRef.current,
    }));

    const handleConfirm = async (okayFlag: boolean, postResult?: any) => {
      if (!okayFlag) {
        onClose(undefined);
        return;
      }

      if (validation) {
        const isValid = await internalRef.current?.checkValidity?.();
        const flag = isValid.flag;
        const message = isValid.message;
        if (flag === false) {
          if (message) await showDialog("alter", message);
          return;
        }
      }

      let rtnCheck = false;

      const selectedValue =
        postResult ?? (await internalRef.current?.getSelectedValue?.());

      if (
        selectedValue &&
        typeof selectedValue === "object" &&
        selectedValue.code
      ) {
        const { code, data } = selectedValue;
        rtnCheck = true;

        switch (code) {
          case "POPUP_PASSWORD":
            if (data === "empty") {
              setTimeout(async () => {
                await showDialog("alter", "비밀번호를 입력해 주세요.");
                internalRef.current?.focusToErrorInput?.();
              }, 0);
              internalRef.current?.focusToErrorInput?.();
              return;
            } else if (data === "notConfirm") {
              setTimeout(async () => {
                await showDialog("alter", "비밀번호가 일치하지 않습니다.");
              }, 0);
              return;
            } else if (data === "error") {
              internalRef.current?.focusToErrorInput?.();
              return;
            } else {
              onClose(data);
            }
            break;

          case "POPUP_CHANGEPWD":
            if (data === "empty") {
              setTimeout(async () => {
                await showDialog("alter", "비밀번호를 모두 입력해 주세요.");
                internalRef.current?.focusToCurrentPwdInput?.();
              }, 0);
              return;
            } else if (data === "notConfirm") {
              setTimeout(async () => {
                await showDialog(
                  "alter",
                  "새 비밀번호와 새 비밀번호 확인이 일치하지 않습니다."
                );
                internalRef.current?.focusToNewPwdInput?.();
              }, 0);
              return;
            } else if (data === "notMatch") {
              setTimeout(async () => {
                await showDialog("alter", "현재 비밀번호가 일치하지 않습니다.");
                internalRef.current?.focusToCurrentPwdInput?.();
              }, 0);
              return;
            } else if (data === "policyFail") {
              setTimeout(async () => {
                internalRef.current?.focusToNewPwdInput?.();
              }, 0);
              return;
            } else if (data === "keepOpened") {
              return;
            } else {
              // setTimeout(async () => {
              //   await showDialog("success", "비밀번호 변경이 완료되었습니다.");
              // }, 0);
              onClose(data);
            }
            break;

          case "POPUP_POST":
          default:
            onClose(data);
            break;
        }
        return;
      }

      if (selectedValue) {
        rtnCheck = true;

        // 엑셀업로드 내용
        if (
          selectedValue.excelUpload !== null &&
          selectedValue.excelUpload !== undefined
        ) {
          const closeFlag = selectedValue.excelUpload;
          if (closeFlag) {
            onClose(selectedValue);
            return;
          } else {
            return;
          }
        }

        onClose(selectedValue);
        return;
      }

      const selectedRow = await internalRef.current?.getSelectedRow?.();
      if (Array.isArray(selectedRow) && selectedRow.flat().length > 0) {
        rtnCheck = true;
        onClose(selectedRow);
        return;
      }

      if (!checkData) {
        rtnCheck = true;
        onClose("ok");
        return;
      }

      // 26.08.21 ysw 자식이 선택 관련 API를 아무것도 노출하지 않으면
      // 애초에 "선택"할 대상이 없는 팝업이다(예: 출력물 미리보기 ReportViewer -
      // forwardRef가 아니라서 ref가 null). 이런 팝업에서 선택 경고를 띄우면
      // 확인으로는 절대 닫을 수 없으므로, 취소와 동일하게 그냥 닫는다.
      const hasSelectionApi =
        typeof internalRef.current?.getSelectedValue === "function" ||
        typeof internalRef.current?.getSelectedRow === "function";
      if (!hasSelectionApi) {
        rtnCheck = true;
        onClose("ok");
        return;
      }

      if (!rtnCheck) {
        showDialog("alter", "선택된 내용이 없습니다. 항목을 선택해 주세요.");
      }
    };

    const handleClose = () => {
      onClose("close");
    };

    const renderBody = () => {
      if (children && React.isValidElement(children)) {
        return React.cloneElement(children as React.ReactElement, {
          modalRef: modalRef,
          ref: internalRef,
        });
      }
      return null;
    };

    return (
      <Dialog
        open={true}
        disableScrollLock
        disablePortal
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
        onClose={(e: any, reason) => {
          if (reason === "escapeKeyDown") {
            e.preventDefault();
            handleClose();
          }
        }}
        maxWidth={false}
        PaperComponent={PaperComponent}
        slotProps={{
          root: {
            sx: {
              position: "absolute",
            },
          },
          paper: {
            sx: {
              width: width ?? "50%",
              height: height ?? "90%",
              padding: "15px",
              borderRadius: "8px",
              display: "flex",
              flexDirection: "column",
              boxShadow:
                "0px 11px 15px -7px rgba(0, 0, 0, 0.2), 0px 24px 38px 3px rgba(0, 0, 0, 0.14), 0px 9px 46px 8px rgba(0, 0, 0, 0.12)",
              position: "absolute",
              margin: 0,
            },
          },
        }}
      >
        <DialogTitle
          style={{ cursor: "move" }}
          className="draggable-modal"
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            padding: 0,
          }}
        >
          {/* DialogTitle이 기본 <h2>라 variant="h6"의 <h6>가 그대로 들어가면
              DOM 중첩 규칙 위반(h6 in h2) 경고가 뜬다. 스타일은 variant로 두고
              태그만 span으로 바꿔 중첩을 없앤다. */}
          <Typography id="modal-title" variant="h6" component="span">
            {title}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            onTouchEnd={handleClose}
            sx={{ marginLeft: "auto" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          ref={dialogContentRef}
          id="modal-description"
          sx={{
            mt: 1,
            maxWidth: "100%",
            height: "100%",
            overflow: "auto",
            wordWrap: "break-word",
            overflowWrap: "break-word",
            px: 1.5,
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            paddingBottom: 0,
          }}
        >
          {renderBody()}
        </DialogContent>

        <DialogActions sx={{ justifyContent: "center" }}>
          {okayBtn && (
            <Button
              onClick={() => handleConfirm(true)}
              onTouchEnd={() => handleConfirm(true)}
              variant="contained"
              sx={{ width: 80 }}
            >
              {okayBtnText ? okayBtnText : "확인"}
            </Button>
          )}
          {closeBtn && (
            <Button
              onClick={() => handleConfirm(false)}
              onTouchEnd={() => handleConfirm(false)}
              variant="outlined"
              sx={{ width: 80 }}
            >
              {closeBtnText ? closeBtnText : "취소"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );
  }
);

const ResizableModal = forwardRef(
  (
    {
      title,
      onClose,
      children,
      validation,
      width,
      height,
      okayBtn = true,
      closeBtn = true,
      okayBtnText,
      closeBtnText,
      rememberSize = true,
      rememberSizeKey,
    }: ResizableModalProps,
    modalRef: any
  ) => {
    const internalRef = useRef<any>(null);
    const dialogContentRef = useRef<HTMLDivElement>(null);
    // 사이즈 저장 위치: localStorage "popupSize"[팝업을 연 메뉴코드][rememberSizeKey || title]
    // (메뉴 화면의 "menuSize" 와 같은 구조. 한 메뉴 안에서 제목이 같은 팝업만 rememberSizeKey 로 구분)
    const [cdMenu, sizeKey] = useMemo(
      () => [
        globalMenu.getState().activeMenuInfo?.cd_menu || "COMMON",
        rememberSizeKey || title,
      ],
      []
    );

    const geometry = useMemo(() => {
      // px, % 모두 허용 - %는 화면 크기 기준으로 px 환산
      const toPx = (v: string, total: number) =>
        v.trim().endsWith("%") ? (total * parseFloat(v)) / 100 : parseFloat(v);
      let w = toPx(width ?? "1200px", window.innerWidth);
      let h = toPx(height ?? "900px", window.innerHeight);

      if (rememberSize) {
        try {
          const saved = JSON.parse(localStorage.getItem("popupSize") || "{}")?.[
            cdMenu
          ]?.[sizeKey];
          if (saved?.width > 0) w = saved.width;
          if (saved?.height > 0) h = saved.height;
        } catch {}
      }

      // 화면을 넘치면 브라우저 스크롤바가 생기면서 화면 폭이 줄어 가운데가 어긋난다.
      // 스크롤바를 뺀 화면 크기(clientWidth)에서 여백 20px 씩 남기고, 위치는 항상 가운데
      const { clientWidth: vw, clientHeight: vh } = document.documentElement;
      w = Math.min(w, vw - POPUP_GAP * 2);
      h = Math.min(h, vh - POPUP_GAP * 2);
      return {
        width: `${w}px`,
        height: `${h}px`,
        x: (vw - w) / 2,
        y: Math.max(POPUP_GAP, (vh - h) / 2 - 25),
      };
    }, []);

    const handleResizeStop = (w: number, h: number) => {
      if (!rememberSize) return;
      try {
        const all = JSON.parse(localStorage.getItem("popupSize") || "{}");
        all[cdMenu] = { ...all[cdMenu], [sizeKey]: { width: w, height: h } };
        localStorage.setItem("popupSize", JSON.stringify(all));
      } catch {}
    };

    useImperativeHandle(modalRef, () => ({
      handleConfirm: (flag: boolean) => handleConfirm(flag),
      getPopupParent: () => dialogContentRef.current,
    }));

    const handleConfirm = async (okayFlag: boolean) => {
      // 취소버튼
      if (!okayFlag) {
        onClose(undefined);
        return;
      }

      // 유효성 검증
      if (validation) {
        const validResult = await internalRef.current?.checkValidity?.();
        if (!validResult) return;
      }

      const okayData = await internalRef.current?.getOkayData?.();
      if (okayData && typeof okayData === "object") {
        const { closeFlag, msgType, msgText, code, rtnData, postCode } =
          okayData;

        // 특정 팝업 로직
        switch (code) {
          default:
            break;
        }

        // 메세지 띄우기
        if (msgText) {
          await showDialog(msgType ?? "alter", msgText);
          if (closeFlag === false) return;
        }
        // 후처리 로직
        if (internalRef.current?.postProcess) {
          internalRef.current.postProcess(postCode, rtnData);
        }

        if (Array.isArray(rtnData) && rtnData.flat().length > 0) {
          onClose(rtnData);
          return;
        } else {
          if (closeFlag) {
            onClose(rtnData);
            return;
          }

          await showDialog(
            "alter",
            "선택된 내용이 없습니다. 항목을 선택해 주세요."
          );
          return;
        }
      }

      onClose(undefined);
    };

    const handleClose = () => {
      onClose("close");
    };

    const renderBody = () => {
      if (children && React.isValidElement(children)) {
        return React.cloneElement(children as React.ReactElement, {
          modalRef: modalRef,
          ref: internalRef,
        });
      }
      return null;
    };

    return (
      <Dialog
        open={true}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
        onClose={(e: any, reason) => {
          if (reason === "escapeKeyDown") {
            e.preventDefault();
            handleClose();
          }
        }}
        maxWidth={false}
        PaperComponent={(props: any) =>
          ResizablePaperComponent(
            props,
            geometry.width,
            geometry.height,
            geometry.x,
            geometry.y,
            handleResizeStop
          )
        }
        slotProps={{
          root: {
            sx: {
              position: "absolute",
            },
          },
          paper: {
            sx: {
              padding: "15px",
              borderRadius: "8px",
              display: "flex",
              flexDirection: "column",
              boxShadow:
                "0px 11px 15px -7px rgba(0, 0, 0, 0.2), 0px 24px 38px 3px rgba(0, 0, 0, 0.14), 0px 9px 46px 8px rgba(0, 0, 0, 0.12)",
              position: "absolute",
              margin: 0,
              maxWidth: "100%",
              maxHeight: "100%",
            },
          },
        }}
      >
        <DialogTitle
          style={{ cursor: "move" }}
          className="draggable-modal"
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            padding: 0,
          }}
        >
          {/* DialogTitle이 기본 <h2>라 variant="h6"의 <h6>가 그대로 들어가면
              DOM 중첩 규칙 위반(h6 in h2) 경고가 뜬다. 스타일은 variant로 두고
              태그만 span으로 바꿔 중첩을 없앤다. */}
          <Typography id="modal-title" variant="h6" component="span">
            {title}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            onTouchEnd={handleClose}
            sx={{ marginLeft: "auto" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          ref={dialogContentRef}
          id="modal-description"
          sx={{
            mt: 1,
            maxWidth: "100%",
            height: "100%",
            overflow: "auto",
            wordWrap: "break-word",
            overflowWrap: "break-word",
            px: 1.5,
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            paddingBottom: 0,
          }}
        >
          {renderBody()}
        </DialogContent>

        <DialogActions sx={{ justifyContent: "center" }}>
          {okayBtn && (
            <Button
              onClick={() => handleConfirm(true)}
              variant="contained"
              sx={{ width: 80 }}
            >
              {okayBtnText ? okayBtnText : "확인"}
            </Button>
          )}
          {closeBtn && (
            <Button
              onClick={() => handleConfirm(false)}
              variant="outlined"
              sx={{ width: 80 }}
            >
              {closeBtnText ? closeBtnText : "취소"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );
  }
);

export interface PopupModalOptions {
  title: string;
  children: React.ReactNode;
  validation?: boolean;
  width?: string;
  height?: string;
  okayBtn?: boolean;
  closeBtn?: boolean;
  checkData?: boolean;
  okayBtnText?: string;
  closeBtnText?: string;
  rememberSize?: boolean;
  rememberSizeKey?: string;
}

export const showPopupModal = (options: PopupModalOptions): Promise<any> => {
  const container = document.createElement("div");
  document.body.appendChild(container);
  const root = ReactDOM.createRoot(container);
  const modalRef = React.createRef<any>(); // modalRef 생성

  return new Promise((resolve) => {
    const handleClose = (result: any) => {
      setTimeout(() => {
        root.unmount();
        document.body.removeChild(container);
        resolve(result);
      }, 0);
    };

    root.render(
      <CommonModal
        ref={modalRef}
        title={options.title}
        onClose={handleClose}
        children={options.children}
        validation={options.validation}
        width={options.width}
        height={options.height}
        okayBtn={options.okayBtn}
        closeBtn={options.closeBtn}
        checkData={options.checkData}
        okayBtnText={options.okayBtnText}
        closeBtnText={options.closeBtnText}
      />
    );
  });
};

export const showResizablePopupModal = (
  options: PopupModalOptions
): Promise<any> => {
  const container = document.createElement("div");
  document.body.appendChild(container);
  const root = ReactDOM.createRoot(container);
  const modalRef = React.createRef<any>(); // modalRef 생성

  return new Promise((resolve) => {
    const handleClose = (result: any) => {
      setTimeout(() => {
        root.unmount();
        document.body.removeChild(container);
        resolve(result);
      }, 0);
    };

    root.render(
      <ResizableModal
        ref={modalRef}
        title={options.title}
        onClose={handleClose}
        children={options.children}
        validation={options.validation}
        width={options.width}
        height={options.height}
        okayBtn={options.okayBtn}
        closeBtn={options.closeBtn}
        okayBtnText={options.okayBtnText}
        closeBtnText={options.closeBtnText}
        rememberSize={options.rememberSize}
        rememberSizeKey={options.rememberSizeKey}
      />
    );
  });
};
